import { ReactNode, useState, useEffect } from 'react';
import { logger } from '@/utils/logger';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { usePlatformAdmin } from '@/contexts/PlatformAdminContext';
import { CompanySelector } from './CompanySelector';
import { SubscriptionExpired } from '@/components/billing/SubscriptionExpired';
import { CompleteProfileModal } from './CompleteProfileModal';
import { AwaitingApproval } from './AwaitingApproval';
import { CompanySuspended } from './CompanySuspended';
import { supabase } from '@/integrations/supabase/client';

interface ProtectedRouteProps {
  children: ReactNode;
  fallback?: ReactNode;
  loginPath?: string;
}

export const ProtectedRoute = ({ children, fallback, loginPath = '/auth' }: ProtectedRouteProps) => {
  const { user, loading, needsCompany, subscriptionExpired, profile, fetchUserProfile, authError, debugInfo, company } = useAuth();
  const { isPlatformAdmin, isCheckingAdmin } = usePlatformAdmin();
  
  const isProfileIncomplete = profile && !profile.isComplete;

  const hardResetSession = async () => {
    logger.log('🧹 Hard reset de sessão...');
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch (e) { /* ignorar */ }

    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('sb-')) localStorage.removeItem(key);
    });
    Object.keys(sessionStorage).forEach(key => {
      if (key.startsWith('sb-')) sessionStorage.removeItem(key);
    });

    if ('caches' in window) {
      try {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      } catch (e) { /* ignorar */ }
    }

    window.location.href = '/auth';
  };

  // Smart guard: só exibir loading de tela inteira se o usuário ainda NÃO foi hidratado.
  // Se user+profile já existem no contexto (navegação interna), renderizar children imediatamente.
  if ((loading || isCheckingAdmin) && !user) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center space-y-4">
        <div className="flex items-center space-x-2">
          <div className="w-4 h-4 bg-primary rounded-full animate-pulse"></div>
          <div className="w-4 h-4 bg-primary rounded-full animate-pulse animation-delay-150"></div>
          <div className="w-4 h-4 bg-primary rounded-full animate-pulse animation-delay-300"></div>
        </div>
        <div className="text-center space-y-2">
          <p className="text-muted-foreground">Carregando...</p>
          <p className="text-xs text-muted-foreground/70">
            Autenticando e carregando perfil
          </p>
          {debugInfo && (
            <p className="text-sm text-muted-foreground mt-2">{debugInfo}</p>
          )}
        </div>
      </div>
    );
  }

  // Show auth error if exists
  if (authError) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center space-y-6 p-4">
        <div className="text-center max-w-md space-y-4">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          
          <h2 className="text-2xl font-semibold text-foreground">Erro de Autenticação</h2>
          <p className="text-muted-foreground">{authError}</p>
          
          {debugInfo && (
            <div className="text-xs text-muted-foreground bg-muted p-2 rounded">
              Debug: {debugInfo}
            </div>
          )}
          
          <div className="flex flex-col gap-2 pt-4">
            <button 
              onClick={async () => {
                logger.log('🔄 Forçando reload completo com limpeza de caches...');
                if ('caches' in window) {
                  try {
                    const keys = await caches.keys();
                    await Promise.all(keys.map(key => caches.delete(key)));
                  } catch (e) { /* ignorar */ }
                }
                sessionStorage.clear();
                window.location.href = '/auth';
              }}
              className="bg-primary text-primary-foreground px-6 py-3 rounded-md hover:bg-primary/90 font-medium transition-colors"
            >
              Tentar Novamente
            </button>
            
            <button 
              onClick={() => hardResetSession()}
              className="bg-secondary text-secondary-foreground px-6 py-3 rounded-md hover:bg-secondary/80 font-medium transition-colors"
            >
              Limpar Sessão e Tentar
            </button>
          </div>
          
          <p className="text-xs text-muted-foreground pt-2">
            Timestamp: {new Date().toLocaleTimeString()}
          </p>
        </div>
      </div>
    );
  }

  if (!user) {
    return fallback ? <>{fallback}</> : <Navigate to={loginPath} replace />;
  }

  // ✅ NOVO: Verificar estado inconsistente: user existe mas profile não (e não está carregando)
  if (user && !profile && !loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center space-y-6 p-4">
        <div className="text-center max-w-md space-y-4">
          <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
            <svg className="w-8 h-8 text-destructive" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          
          <h2 className="text-2xl font-semibold text-foreground">Sessão Inválida</h2>
          <p className="text-muted-foreground">
            Sua sessão expirou ou os dados da sua conta não foram encontrados no sistema.
          </p>
          
          <button 
            onClick={() => hardResetSession()}
            className="bg-primary text-primary-foreground px-6 py-3 rounded-md hover:bg-primary/90 font-medium transition-colors"
          >
            Fazer Login Novamente
          </button>
          
          <p className="text-xs text-muted-foreground pt-2">
            Se o problema persistir, entre em contato com o administrador.
          </p>
        </div>
      </div>
    );
  }

  // If user is authenticated but doesn't have a company, show company selector
  if (needsCompany) {
    return <CompanySelector />;
  }

  // If subscription is expired, show billing screen
  if (subscriptionExpired) {
    return <SubscriptionExpired />;
  }

  // ✅ NOVO: Verificar status do tenant (KYB) - APÓS needsCompany
  // CRÍTICO: Platform Admins passam direto (evitar lockout)
  const tenantStatus = company?.tenant_status;
  
  if (!isPlatformAdmin && tenantStatus) {
    // Empresa pendente de aprovação
    if (tenantStatus === 'pending_approval') {
      return <AwaitingApproval />;
    }

    // Empresa suspensa ou banida
    if (tenantStatus === 'suspended' || tenantStatus === 'banned') {
      return <CompanySuspended reason={company?.suspended_reason} />;
    }
  }

  // If user needs to complete profile or change password, show unified modal
  const needsProfileCompletion = profile?.must_change_password || isProfileIncomplete;
  if (needsProfileCompletion) {
    return (
      <CompleteProfileModal
        onProfileCompleted={() => window.location.reload()}
        isTemporaryPassword={!!profile?.must_change_password}
      />
    );
  }

  return <>{children}</>;
};